import React, { useState, useEffect } from 'react'
import { BarChart3, Users, TrendingUp, Activity } from 'lucide-react'
import { mdApi } from '../api/mdApi'
import './Analytics.css'

function Analytics() {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadAnalytics()
  }, [])

  const loadAnalytics = async () => {
    try {
      setLoading(true)
      const data = await mdApi.getPopulationHealth()
      setStats(data)
    } catch (error) {
      console.error('Error loading analytics:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading analytics...</p>
      </div>
    )
  }

  if (!stats) {
    return <div>No analytics data available</div>
  }

  return (
    <div className="analytics">
      <div className="analytics-header">
        <h1>Population Health Analytics</h1>
        <p className="analytics-subtitle">
          Practice-wide insights and trends for {stats.total_patients} patients
        </p>
      </div>

      <div className="analytics-grid">
        {/* Risk Distribution */}
        <div className="analytics-card">
          <h2>
            <BarChart3 size={20} />
            Risk Distribution
          </h2>
          <div className="risk-bars">
            {Object.entries(stats.risk_distribution || {}).map(([level, data]) => (
              <div key={level} className="risk-bar-item">
                <div className="risk-bar-header">
                  <span className="risk-label">{level.charAt(0).toUpperCase() + level.slice(1)}</span>
                  <span className="risk-value">{data.count} ({data.percentage}%)</span>
                </div>
                <div className="risk-bar">
                  <div 
                    className={`risk-bar-fill risk-${level}`}
                    style={{ width: `${data.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Conditions */}
        <div className="analytics-card">
          <h2>
            <Users size={20} />
            Top Chronic Conditions
          </h2>
          <div className="conditions-list">
            {stats.top_conditions?.map((condition, idx) => (
              <div key={idx} className="condition-item">
                <span className="condition-name">{condition.condition}</span>
                <span className="condition-stats">
                  {condition.count} patients ({condition.percentage}%)
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Adherence Metrics */}
        <div className="analytics-card">
          <h2>
            <TrendingUp size={20} />
            Medication Adherence
          </h2>
          <div className="adherence-stats">
            <div className="adherence-metric">
              <span className="adherence-label">Average</span>
              <span className="adherence-value">{stats.adherence_metrics?.medication?.average}%</span>
            </div>
            <div className="adherence-breakdown">
              <div className="adherence-category">
                <span>Excellent (≥90%)</span>
                <strong>{stats.adherence_metrics?.medication?.excellent}</strong>
              </div>
              <div className="adherence-category">
                <span>Good (70-89%)</span>
                <strong>{stats.adherence_metrics?.medication?.good}</strong>
              </div>
              <div className="adherence-category">
                <span>Needs Improvement (&lt;70%)</span>
                <strong>{stats.adherence_metrics?.medication?.needs_improvement}</strong>
              </div>
            </div>
          </div>
        </div>

        {/* Wearable Adoption */}
        <div className="analytics-card">
          <h2>
            <Activity size={20} />
            Wearable Device Adoption
          </h2>
          <div className="wearable-stats">
            <div className="wearable-metric">
              <span className="wearable-label">Adoption Rate</span>
              <span className="wearable-value">{stats.wearable_adoption?.adoption_rate}%</span>
            </div>
            <div className="wearable-metric">
              <span className="wearable-label">Patients with Devices</span>
              <span className="wearable-value">{stats.wearable_adoption?.patients_with_devices}</span>
            </div>
            <div className="wearable-devices">
              <h3>Device Types</h3>
              {Object.entries(stats.wearable_adoption?.device_types || {}).map(([device, count]) => (
                <div key={device} className="device-item">
                  <span>{device}</span>
                  <strong>{count}</strong>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Analytics